import { type Component, type JSX, createEffect, onCleanup } from 'solid-js';
import styles from './BottomSheet.module.css';

export interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: JSX.Element;
}

/**
 * Reusable bottom sheet component.
 * Slides up from the bottom with spring animation.
 * Supports backdrop click to dismiss, drag handle for mobile,
 * and keyboard dismiss (Escape).
 */
const BottomSheet: Component<BottomSheetProps> = (props) => {
  // Close on Escape key
  createEffect(() => {
    if (!props.isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        props.onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    // Prevent body scroll when sheet is open
    document.body.style.overflow = 'hidden';

    onCleanup(() => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    });
  });

  const handleBackdropClick = () => {
    props.onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        class={`${styles.backdrop} ${props.isOpen ? styles.backdropOpen : ''}`}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        class={`${styles.sheet} ${props.isOpen ? styles.sheetOpen : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={props.title}
      >
        {/* Drag handle */}
        <div class={styles.dragHandle} aria-hidden="true">
          <div class={styles.dragBar} />
        </div>

        {/* Header */}
        <div class={styles.header}>
          <h2 class={styles.title}>{props.title}</h2>
          <button
            class={styles.closeButton}
            onClick={() => props.onClose()}
            aria-label="Close"
            type="button"
          >
            &#x2715;
          </button>
        </div>

        {/* Content */}
        <div class={styles.content}>
          {props.children}
        </div>
      </div>
    </>
  );
};

export default BottomSheet;
